import { AggregateFunctionName, RunDto } from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { AggregateFunctionChart } from "@features/results/Result/AggregateFunctionChart"
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material"
import {
  useGetRunAggregatesQuery,
  useGetRunsNodesResultsHistoricAggregateQuery,
} from "@store/services/runService"
import { areHistoricResultsOfTheSameUnitFormat } from "@utils/run"
import { Chart, Point } from "chart.js"
import { useRef } from "react"
import { useLocalStorage } from "usehooks-ts"

type AggregateFunctionChartContainerProps = {
  runs: RunDto[]
  isDownloadChartImageButtonDisabled: boolean
  node: GroupNodeLabel
}

const AggregateFunctionChartContainer = (props: AggregateFunctionChartContainerProps) => {
  const [selectedAgg, setSelectedAgg] = useLocalStorage(`aggregateFunctionOptions`, ["mean"])

  const runKey = props.runs[0]?.key ?? ""
  const runKeys = props.runs.map((run) => run.key)

  const { data: aggs } = useGetRunAggregatesQuery({ key: runKey }, { skip: !runKey })
  const { data: aggregateResultsResponse } = useGetRunsNodesResultsHistoricAggregateQuery(
    {
      runKeys,
      nodes: [graphViewGroupNodeLabelToSampleNode(props.node.label)],
    },
    {
      skip: !runKey,
    },
  )

  const aggregateChartContainerRef = useRef<HTMLDivElement | null>(null)
  const aggregateChartRef = useRef<Chart<"line", (number | Point | null)[], unknown> | undefined>(
    undefined,
  )

  const areTheSameUnitsUsed = areHistoricResultsOfTheSameUnitFormat(
    aggregateResultsResponse?.results,
  )

  if (!areTheSameUnitsUsed) {
    return (
      <Typography>
        Can not render <strong>aggregate chart</strong> because selected node(s) are using different
        units.
      </Typography>
    )
  }

  const versionedRuns = props.runs.map((run) => ({
    ...run,
    run_name: `${run.run_name} (${run.version})`,
  }))

  const handleResetZoom = () => {
    if (!aggregateChartRef.current) return

    aggregateChartRef.current.resetZoom()
  }

  return (
    <>
      <Grid item xs={3} sx={{ mt: 5 }}>
        <FormControl fullWidth={true}>
          <InputLabel id="agg-label">Aggregate function</InputLabel>
          <Select
            multiple
            labelId="agg-label"
            value={selectedAgg}
            disabled={!aggs?.[0]?.data}
            label="Aggregate function"
            onChange={(value) =>
              setSelectedAgg(
                Array.isArray(value.target.value) ? value.target.value : [value.target.value],
              )
            }
            size={"small"}
          >
            {aggs?.[0]?.data?.map((item: string) =>
              ["hist", "hist_edges", "skew", "kurtosis"].indexOf(item) < 0 ? (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ) : null,
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={9} sx={{ mt: 5 }}>
        <Button onClick={handleResetZoom} variant={"contained"} sx={{ ml: 2 }}>
          Reset zoom
        </Button>
        <DownloadChartImageDialog
          buttonProps={{ disabled: props.isDownloadChartImageButtonDisabled, sx: { ml: 1 } }}
          chartRef={aggregateChartContainerRef}
          defaultFileName="Aggregate Chart"
          runReference={runKeys.join(" vs. ")}
        />
      </Grid>
      <Grid item xs={12} ref={aggregateChartContainerRef}>
        <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
          Aggregations for <strong>{props.node.label}</strong>{" "}
        </Typography>
        <AggregateFunctionChart
          runs={versionedRuns}
          node={props.node}
          aggregationFunc={selectedAgg as AggregateFunctionName[]}
          ref={aggregateChartRef}
        />
      </Grid>
    </>
  )
}

export default AggregateFunctionChartContainer
